import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../config';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import StaffDetail from './StaffDetail';
import './NotificationsPage.css';

function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [showStaffDetail, setShowStaffDetail] = useState(false);
  const userEnvironment = localStorage.getItem('environment') || 'production';

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/api/notifications');
        
        if (response.data) {
          // Parse the data field for each notification
          const parsedNotifications = response.data.map(notification => ({
            ...notification,
            data: typeof notification.data === 'string' 
              ? JSON.parse(notification.data) 
              : notification.data
          }));
          
          setNotifications(parsedNotifications);
        }
      } catch (err) {
        console.error('Error fetching notifications:', err);
        toast.error('Failed to load notifications');
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await axiosInstance.put(`/api/notifications/${notificationId}/read`);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read_at: new Date().toISOString() } 
            : notification
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axiosInstance.put('/api/notifications/read-all');
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({
          ...notification,
          read_at: notification.read_at || new Date().toISOString()
        }))
      );
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  // Handle view staff details
  const handleViewStaff = (staffId, e) => {
    if (e) e.preventDefault(); // Prevent default link behavior
    setSelectedStaffId(staffId);
    setShowStaffDetail(true);
  };

  // Handle staff detail close
  const handleStaffDetailClose = () => {
    setShowStaffDetail(false);
    setSelectedStaffId(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get notification types for filter
  const getNotificationTypes = () => {
    const types = new Set();
    notifications.forEach(notification => {
      types.add(notification.type);
    });
    return Array.from(types);
  };

  // Get priority class based on notification priority
  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'high':
        return 'priority-high';
      case 'medium':
        return 'priority-medium';
      case 'low':
        return 'priority-low';
      default:
        return '';
    }
  };

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'LICENSE_STATUS_CHANGE':
        return '🔄';
      case 'LICENSE_EXPIRING':
        return '⏱️';
      case 'POINTS_CHANGE':
        return '🚫';
      case 'NEW_STAFF':
        return '👤';
      case 'CHECK_LIMIT_WARNING':
        return '⚠️';
      case 'SYSTEM_UPDATE':
        return '🔔';
      default:
        return '📌';
    }
  };

  // Render notification content based on type
  const renderNotificationContent = (notification) => {
    const { type, message, data } = notification;
    
    // If data is not properly parsed, use the message directly
    if (!data || typeof data !== 'object') {
      return message;
    }
    
    switch (type) {
      case 'LICENSE_STATUS_CHANGE':
        return (
          <>
            <strong>{data.staff_name || notification.first_name + ' ' + notification.last_name || 'Unknown user'}'s</strong> license status changed from{' '}
            <span className={`status ${data.old_status?.toLowerCase()}`}>
              {data.old_status || 'Unknown'}
            </span> to{' '}
            <span className={`status ${data.new_status.toLowerCase()}`}>
              {data.new_status}
            </span>
          </>
        );
        
      case 'LICENSE_EXPIRING':
        return (
          <>
            <strong>{data.staff_name || notification.first_name + ' ' + notification.last_name || 'Unknown user'}'s</strong> license will expire in{' '}
            <strong>{data.days_remaining} days</strong> ({data.expiry_date})
          </>
        );
        
      case 'POINTS_CHANGE':
        return (
          <>
            <strong>{data.staff_name || notification.first_name + ' ' + notification.last_name || 'Unknown user'}'s</strong> penalty points changed from{' '}
            <span className={`points points-${data.old_points > 6 ? 'high' : data.old_points > 3 ? 'medium' : 'low'}`}>
              {data.old_points}
            </span> to{' '}
            <span className={`points points-${data.new_points > 6 ? 'high' : data.new_points > 3 ? 'medium' : 'low'}`}>
              {data.new_points}
            </span>
          </>
        );
        
      case 'NEW_STAFF':
        return (
          <>
            New driver <strong>{data.staff_name}</strong> has been added to your account
          </>
        );
        
      case 'CHECK_LIMIT_WARNING':
        return (
          <>
            You have used <strong>{data.used_checks}</strong> of your{' '}
            <strong>{data.total_checks}</strong> monthly license checks
          </>
        );
        
      case 'SYSTEM_UPDATE':
        return (
          <>
            <strong>System Update:</strong> {data.message}
          </>
        );
        
      case 'NEW_ENDORSEMENT':
        return (
          <>
            New endorsement ({data.endorsement_code}) with {data.points} points added to{' '}
            <strong>{data.staff_name || notification.first_name + ' ' + notification.last_name || 'Unknown user'}'s</strong> license
          </>
        );
        
      case 'INACTIVE_STAFF':
        return (
          <>
            <strong>{data.staff_name || notification.first_name + ' ' + notification.last_name || 'Unknown user'}'s</strong> license hasn't been checked in over 90 days
          </>
        );
        
      default:
        return message;
    }
  };

  // Filter and sort notifications
  const filteredNotifications = notifications
    .filter(notification => {
      // Apply read/unread filter
      if (filter === 'read' && !notification.read_at) return false;
      if (filter === 'unread' && notification.read_at) return false;
      
      // Apply type filter
      if (typeFilter !== 'all' && notification.type !== typeFilter) return false;
      
      return true;
    })
    .sort((a, b) => {
      // Apply sort order
      if (sortOrder === 'newest') {
        return new Date(b.created_at) - new Date(a.created_at);
      } else {
        return new Date(a.created_at) - new Date(b.created_at);
      }
    });

  if (loading) {
    return <div className="notifications-loading">Loading notifications...</div>;
  }

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <h2>Notifications</h2>
        <button 
          className="mark-all-read-btn" 
          onClick={markAllAsRead}
          disabled={!notifications.some(n => !n.read_at)}
        >
          Mark all as read
        </button>
      </div>

      <div className="notifications-filters">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Type:</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All Types</option>
            {getNotificationTypes().map(type => (
              <option key={type} value={type}>
                {type.replace(/_/g, ' ').toLowerCase()
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label>Sort:</label>
          <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
      </div>

      {filteredNotifications.length === 0 ? (
        <div className="no-notifications">
          No notifications match your filters
        </div>
      ) : (
        <div className="notifications-list">
          {filteredNotifications.map(notification => (
            <div 
              key={notification.id} 
              className={`notification-item ${notification.read_at ? 'read' : 'unread'} ${getPriorityClass(notification.priority)}`}
            >
              <div className="notification-header">
                <div className="notification-type">
                  {getNotificationIcon(notification.type)}{' '}
                  {notification.type.replace(/_/g, ' ').toLowerCase()
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                </div>
                <div className="notification-date">
                  {formatDate(notification.created_at)}
                </div>
              </div>
              
              <div className="notification-body">
                {renderNotificationContent(notification)}
              </div>
              
              <div className="notification-actions">
                {notification.staff_id && (
                  <button 
                    className="view-staff-btn"
                    onClick={(e) => handleViewStaff(notification.staff_id, e)}
                  >
                    View Staff
                  </button>
                )}
                
                {!notification.read_at && (
                  <button 
                    className="mark-read-btn"
                    onClick={() => markAsRead(notification.id)}
                  >
                    Mark as read
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Staff Detail Modal */}
      {showStaffDetail && selectedStaffId && createPortal(
        <StaffDetail
          staffId={selectedStaffId}
          token={localStorage.getItem('token')}
          environment={userEnvironment}
          onClose={handleStaffDetailClose}
        />,
        document.body
      )}
    </div>
  );
}

export default NotificationsPage; 